import { FC, useEffect, useState, useMemo } from "react";
import StoryblokClient from "storyblok-js-client";
import { Spinner } from "flowbite-react";
import { QAItem } from "../QAItem/QAItem";
import { StoryblokRichtext } from "storyblok-rich-text-react-renderer";
import { useTranslation } from "react-i18next";
import { HiOutlineExternalLink } from "react-icons/hi";
import { useHelpPopupContext } from "../../contexts/HelpPopupContext";
import { StoryblokRichTextRenderer, Button } from "@components";

// Define the expected structure of the Storyblok data
// Based on user input: title(text), description(richtext), faq(blocks->QAItem),
// link_title(text), link_url(link), video_title(text), video_url(text)
// QAItem: question(text), answer(richtext), expandedByDefault(boolean)
interface StoryblokLink {
  id: string;
  cached_url: string;
  fieldtype: "multilink";
  linktype: "url" | "story" | "email" | "asset";
  url: string;
  target?: "_blank" | "_self";
  // Other fields might exist depending on link type
}

// For regular nested blocks
interface StoryblokQAItem {
  _uid: string;
  component: string;
  question: string;
  answer: StoryblokRichtext;
  expandedByDefault?: boolean;
}

// For multi-option references
interface StoryblokQAReference {
  _uid: string;
  uuid: string;
  content: {
    _uid: string;
    component: string;
    question: string;
    answer: StoryblokRichtext;
    expandedByDefault?: boolean;
  };
}

// For related items array in the Storyblok API response
interface StoryblokRelatedItem {
  uuid: string;
  content: {
    _uid: string;
    component: string;
    question: string;
    answer: StoryblokRichtext;
    expandedByDefault?: boolean;
  };
}

interface StoryblokStory {
  uuid: string;
  content: any;
  [key: string]: any;
}

interface HelpBlockStory {
  id: number;
  uuid: string;
  name: string;
  slug: string;
  content: {
    _uid: string;
    component: string;
    title?: string;
    description?: StoryblokRichtext;
    faq?: (
      | StoryblokQAItem
      | StoryblokQAReference
      | string
      | { uuid: string }
    )[]; // Can be different formats
    link_title?: string;
    link_url?: StoryblokLink;
    video_title?: string;
    video_url?: string;
  };
  rels?: StoryblokRelatedItem[]; // Related items for references
  // Other Storyblok metadata...
}

interface StoryblokHelpBlockProps {
  storySlug: string;
  accessToken: string; // Pass token as prop for flexibility
}

// Helper function to get YouTube embed URL from various YouTube URL formats
const getYouTubeEmbedUrl = (url: string): string | null => {
  if (!url) return null;

  // Match YouTube URL patterns
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);

  return match && match[2].length === 11
    ? `https://www.youtube.com/embed/${match[2]}?autoplay=0&rel=0`
    : null;
};

// Helper function to detect video type and return proper embed URL
const getVideoEmbedUrl = (
  url: string,
): { url: string | null; type: "youtube" | "vimeo" | "unknown" } => {
  if (!url) return { url: null, type: "unknown" };

  // Check for YouTube
  if (url.includes("youtube.com") || url.includes("youtu.be")) {
    return { url: getYouTubeEmbedUrl(url), type: "youtube" };
  }

  // Check for Vimeo
  const vimeoRegExp =
    /vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|)(\d+)(?:$|\/|\?)/;
  const vimeoMatch = url.match(vimeoRegExp);

  if (vimeoMatch) {
    return {
      url: `https://player.vimeo.com/video/${vimeoMatch[1]}`,
      type: "vimeo",
    };
  }

  // Unknown video type, return original URL
  return { url: url, type: "unknown" };
};

export const StoryblokHelpBlock: FC<StoryblokHelpBlockProps> =
  function StoryblokHelpBlock({ storySlug, accessToken }) {
    const { t, i18n } = useTranslation(["common", "help"]);
    const [story, setStory] = useState<HelpBlockStory | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const helpPopupContext = useHelpPopupContext();

    const externalLink = useMemo(() => {
      const link_data = story?.content?.link_url;
      const link_title = story?.content?.link_title;

      if (link_data?.url && link_title) {
        let finalUrl = link_data.url;
        const linktype = link_data.linktype;
        const target = link_data.target || "_blank";

        if (linktype === "url") {
          // Ensure external URLs are absolute
          if (
            !finalUrl.includes("://") &&
            !finalUrl.startsWith("mailto:") &&
            !finalUrl.startsWith("tel:")
          ) {
            // Prepend https:// if no protocol is present
            finalUrl = `https://${finalUrl}`;
          }
        } else if (linktype === "email") {
          // Ensure mailto: prefix for email links
          if (!finalUrl.startsWith("mailto:")) {
            finalUrl = `mailto:${finalUrl}`;
          }
        }
        // For linktype 'story' or 'asset', we assume the URL is correct as provided by Storyblok

        const handleClick = () => {
          window.open(finalUrl, target);
        };

        return (
          <Button
            variant="secondary"
            size="sm"
            onClick={handleClick}
            icon={<HiOutlineExternalLink className="h-4 w-4" />}
            className="text-secondary"
          >
            {link_title}
          </Button>
        );
      }
      return null;
    }, [story]);

    useEffect(() => {
      const fetchStory = async () => {
        setLoading(true);
        setError(null);

        try {
          const storyblokApi = new StoryblokClient({
            accessToken,
            cache: {
              clear: "auto",
              type: "memory",
            },
            timeout: 10000,
          });
          const cacheVersion = Date.now();

          // Get current language and language mapping
          // Set language code for Storyblok (map i18n language codes to Storyblok language codes if needed)
          const currentLanguage = i18n.language;
          // Use 'en' for English, 'no' for Norwegian, fallback to 'default' if language isn't supported
          let languageCode = "default";

          if (currentLanguage === "en") {
            languageCode = "en";
          } else if (currentLanguage === "no") {
            languageCode = "no";
          }

          // Try to fetch content in the current language
          let response = await storyblokApi.get(`cdn/stories/${storySlug}`, {
            version: "published",
            resolve_relations: "helpPopup.faq,help_item.faq,faq_item,qa_item",
            resolve_links: "url",
            cv: cacheVersion,
            language: languageCode !== "default" ? languageCode : undefined,
          });

          let data = response.data;

          // If no content found in the requested language, try the default language
          if (!data.story && languageCode !== "default") {
            response = await storyblokApi.get(`cdn/stories/${storySlug}`, {
              version: "published",
              resolve_relations: "helpPopup.faq,help_item.faq,faq_item,qa_item",
              resolve_links: "url",
              cv: cacheVersion,
            });

            data = response.data;
          }

          if (
            data.story?.content?.faq?.length > 0 &&
            (!data.rels || data.rels.length === 0)
          ) {
            const faqIds = data.story.content.faq
              .filter(
                (item: unknown) =>
                  typeof item === "string" ||
                  (typeof item === "object" && item !== null && "uuid" in item),
              )
              .map((item: unknown) =>
                typeof item === "string"
                  ? item
                  : (item as { uuid: string }).uuid,
              );

            if (faqIds.length > 0) {
              try {
                const faqResponse = await storyblokApi.get("cdn/stories", {
                  version: "published",
                  by_uuids: faqIds.join(","),
                  cv: cacheVersion,
                  language:
                    languageCode !== "default" ? languageCode : undefined,
                });

                if (faqResponse.data?.stories?.length > 0) {
                  data.rels = faqResponse.data.stories
                    .map((story: StoryblokStory) => {
                      if (
                        story.content?.component === "qa_item" ||
                        story.content?.component === "faq_item" ||
                        story.content?.component === "qaItem"
                      ) {
                        return {
                          uuid: story.uuid,
                          content: story.content,
                        };
                      }

                      if (
                        story.content?.faq &&
                        Array.isArray(story.content.faq)
                      ) {
                        return {
                          uuid: story.uuid,
                          content: {
                            _uid: story.content._uid || story.uuid,
                            component: "qa_item",
                            question:
                              story.content.question ||
                              story.content.title ||
                              "FAQ Item",
                            answer: story.content.answer ||
                              story.content.description || {
                                type: "doc",
                                content: [],
                              },
                            expandedByDefault: false,
                          },
                        };
                      }

                      return {
                        uuid: story.uuid,
                        content: story.content,
                      };
                    })
                    .filter((rel: any) => rel !== null);
                }
              } catch (faqErr) {
                console.error("Error fetching FAQ items:", faqErr);
              }
            }
          }

          if (data.story) {
            setStory(data.story);
          } else {
            setError(
              t("help:error.story_not_found", "Help content not found."),
            );
          }
        } catch (err) {
          console.error("Error fetching Storyblok story:", err);
          setError(
            t("help:error.fetch_failed", "Failed to load help content."),
          );
        } finally {
          setLoading(false);
        }
      };

      if (storySlug && accessToken) {
        fetchStory();
      }
    }, [storySlug, accessToken, t, i18n.language]);

    useEffect(() => {
      if (helpPopupContext) {
        helpPopupContext.setExternalLink(externalLink);
      }
      return () => {
        if (helpPopupContext) {
          helpPopupContext.setExternalLink(null);
        }
      };
    }, [externalLink, helpPopupContext]);

    if (loading) {
      return (
        <div className="flex justify-center items-center p-6">
          <Spinner
            color="purple"
            size="lg"
            aria-label={t("common:loading", "Loading...")}
          />
        </div>
      );
    }

    if (error) {
      return (
        <div className="p-6 text-center">
          <div className="mb-4 text-red-500 dark:text-red-400">
            <svg
              className="w-12 h-12 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <p className="text-secondary">{error}</p>
        </div>
      );
    }

    if (!story?.content) {
      return (
        <div className="p-6 text-center">
          <p className="text-muted">
            {t("help:error.no_content", "No content available.")}
          </p>
        </div>
      );
    }

    const { title, description, faq, video_url } = story.content;

    const processedFaq =
      (faq
        ?.map((item) => {
          if (typeof item === "string") {
            if (story.rels?.length) {
              const relatedItem = story.rels.find((rel) => rel.uuid === item);
              if (relatedItem) {
                return {
                  _uid: item,
                  uuid: item,
                  content: relatedItem.content,
                };
              }
            }

            console.warn("Could not resolve reference for UUID:", item);
            return null;
          }

          if (typeof item !== "object" || item === null) {
            console.warn(`Invalid FAQ item format:`, item);
            return null;
          }

          if (
            "uuid" in item &&
            !("_uid" in item) &&
            !("content" in item) &&
            story.rels?.length
          ) {
            const uuid = item.uuid;
            const relatedItem = story.rels.find((rel) => rel.uuid === uuid);
            if (relatedItem) {
              return {
                _uid: uuid,
                uuid: uuid,
                content: relatedItem.content,
              };
            }
            console.warn("Could not find related item for UUID:", uuid);
            return null;
          }

          if (!("_uid" in item) && !("uuid" in item)) {
            console.warn("Missing _uid or uuid in FAQ item:", item);
            return null;
          }

          return item;
        })
        .filter(Boolean) as (StoryblokQAItem | StoryblokQAReference)[]) || [];

    const videoData = video_url
      ? getVideoEmbedUrl(video_url)
      : { url: null, type: "unknown" };
    const videoEmbed = videoData.url ? (
      <div className="w-full mb-6 rounded-lg overflow-hidden shadow-md">
        <div className="relative pb-[56.25%] h-0">
          <iframe
            src={videoData.url}
            title="Video"
            className="absolute top-0 left-0 w-full h-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          ></iframe>
        </div>
      </div>
    ) : null;

    return (
      <div className="max-w-3xl mx-auto">
        {title && (
          <header className="mb-6 border-b border-border pb-4">
            <h2 className="text-xl font-semibold text-text">
              {title}
            </h2>
          </header>
        )}

        {description && (
          <div className="mb-6">
            <StoryblokRichTextRenderer document={description} />
          </div>
        )}

        {videoEmbed}

        {processedFaq.length > 0 ? (
          <div className="mb-6">
            <div className="bg-surface-muted/50 rounded-lg p-4">
              <h3 className="text-base font-medium text-text mb-4 flex items-center">
                <span className="mr-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900/30">
                  <span className="text-purple-600 dark:text-purple-400">
                    ?
                  </span>
                </span>
                {t(
                  "help:faq_title",
                  i18n.language === "no"
                    ? "Ofte stilte spørsmål"
                    : "Frequently Asked Questions",
                )}
              </h3>
              <div className="divide-y divide-border">
                {processedFaq.map((item) => {
                  const isReference = "content" in item;
                  const itemKey = isReference
                    ? item._uid || item.uuid
                    : item._uid;

                  const itemProps = {
                    _uid: isReference ? item._uid || item.uuid : item._uid,
                    question: isReference
                      ? item.content.question
                      : item.question,
                    answer: isReference ? item.content.answer : item.answer,
                    expandedByDefault: isReference
                      ? !!item.content.expandedByDefault
                      : !!item.expandedByDefault,
                  };

                  return <QAItem key={itemKey} {...itemProps} />;
                })}
              </div>
            </div>
          </div>
        ) : faq && faq.length > 0 ? (
          <div className="mb-6">
            <div className="bg-surface-muted/50 rounded-lg p-4">
              <h3 className="text-base font-medium text-text mb-4 flex items-center">
                <span className="mr-2 inline-flex items-center justify-center w-6 h-6 rounded-full bg-purple-100 dark:bg-purple-900/30">
                  <span className="text-purple-600 dark:text-purple-400">
                    ?
                  </span>
                </span>
                {t(
                  "help:faq_title",
                  i18n.language === "no"
                    ? "Ofte stilte spørsmål"
                    : "Frequently Asked Questions",
                )}
              </h3>
              <div className="text-center py-4 text-muted">
                <p>
                  {t(
                    "help:faq_unavailable",
                    "FAQ content is currently unavailable.",
                  )}
                </p>
                <p className="mt-2 text-sm">
                  {t(
                    "help:try_again_later",
                    "Please try again later or contact support.",
                  )}
                </p>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    );
  };
